// Change email — the M3 double-opt-in token flow, finally wired up.
//
// The `change-email` template has been seeded in AR + EN since M3 with ZERO
// call sites. This screen drives it: request → link lands in the NEW inbox →
// the API bounces the click back here with ?emailToken=… → we POST it with
// credentials. The users row is not touched until that round trip completes,
// so a typo can never lock a parent out of their own recovery address.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Button, Field, Input } from '@fajrarabi/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';

import { AccountSection } from './AccountSection';

import { confirmEmailChange, requestEmailChange } from '~/lib/account';
import { ApiError } from '~/lib/api';
import { useAuth } from '~/lib/auth';

/**
 * Read `?emailToken=` ONCE and strip it, so a refresh cannot re-submit a
 * spent single-use token and show a spurious "link expired". Same pattern the
 * billing screen uses for `?checkout=`.
 */
function readEmailToken(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('emailToken');
}

export function ChangeEmailCard() {
  const t = useT();
  const { user, refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);

  // Captured on mount, before the effect below strips the query string.
  const [pendingToken] = useState(readEmailToken);

  const request = useMutation({
    mutationFn: () => requestEmailChange(email, password),
    onSuccess: () => {
      setEmail('');
      setPassword('');
      setSent(true);
    },
  });

  const confirm = useMutation({
    mutationFn: (token: string) => confirmEmailChange(token),
    // The JWT carries `email`; the API re-issued it, so re-read /auth/me
    // rather than leaving the header showing the old address.
    onSuccess: () => refresh(),
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !pendingToken) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('emailToken');
    window.history.replaceState({}, '', url.toString());
    confirm.mutate(pendingToken);
    // Fire exactly once for the token captured at mount.
    // NOTE: deliberately omitted from the dep array (see above).
    // eslint-plugin-react-hooks is not installed; restore the
    // `react-hooks/exhaustive-deps` disable directive when it is.
  }, [pendingToken]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSent(false);
    request.mutate();
  }

  const busy = request.isPending || confirm.isPending;

  return (
    <AccountSection
      id="account-email"
      title={t('app.account.email_title')}
      description={t('app.account.email_description')}
    >
      {/* Confirming a token from the emailed link. */}
      {confirm.isPending && <Alert tone="info">{t('app.account.email_verifying')}</Alert>}
      {confirm.isSuccess && <Alert tone="success">{t('app.account.email_verified_success')}</Alert>}
      {confirm.error && <Alert tone="danger">{messageFor(confirm.error, t)}</Alert>}

      <p className="text-sm text-ink-500">
        {t('app.account.email_current')}
        {': '}
        <span className="font-medium text-ink-900">{user?.email ?? '—'}</span>
      </p>

      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t('app.account.email_label')}>
          {(id) => (
            <Input
              id={id}
              type="email"
              autoComplete="email"
              inputMode="email"
              dir="ltr"
              value={email}
              required
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>

        <Field label={t('app.account.password_current_label')}>
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              value={password}
              required
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>

        {request.error && <Alert tone="danger">{messageFor(request.error, t)}</Alert>}
        {sent && <Alert tone="success">{t('app.account.email_sent')}</Alert>}

        <div className="flex justify-end">
          <Button type="submit" disabled={busy}>
            {t('app.account.email_submit')}
          </Button>
        </div>
      </form>
    </AccountSection>
  );
}

function messageFor(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError) {
    if (err.status === 409) return t('app.account.email_error_taken');
    if (err.status === 401) return t('app.account.password_error_wrong');
    if (err.code === 'invalid_input') return t('app.account.email_error_same');
  }
  return t('app.common.error_generic');
}
